import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

const pmcLegendDefs: Record<string, string> = {
  'ATL (Fatigue)': 'Acute Training Load \u2014 exponentially weighted average of recent training stress (7-day). High ATL = high recent workload.',
  'CTL (Fitness)': 'Chronic Training Load \u2014 exponentially weighted average of training stress (42-day). Represents your current fitness level.',
  'TSB (Form)': 'Training Stress Balance = CTL - ATL. Positive = fresh/rested. Negative = fatigued. Best race performance at +5 to +25.',
};

interface PMCPoint {
  date: string;
  ctl: number;
  atl: number;
  tsb: number;
}

type PMCRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: PMCRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const formatDateTick = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    const day = d.getDate();
    const mon = d.toLocaleDateString('en-US', { month: 'short' });
    const yr = String(d.getFullYear()).slice(-2);
    return `${day} ${mon} '${yr}`;
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.stroke || '#333', marginBottom: 2 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </div>
      ))}
    </div>
  );
};

interface PMCChartProps {
  targetUserId?: number | null;
}

const PMCChart: React.FC<PMCChartProps> = ({ targetUserId }) => {
  const [range, setRange] = useState<PMCRange>('90d');
  const [data, setData] = useState<PMCPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (r: PMCRange) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range: r });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/pmc?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load PMC data');
      const json = await res.json();
      const today = new Date().toISOString().slice(0, 10);
      // Filter out future dates and trim trailing all-zero points
      let points: PMCPoint[] = (json.points || []).filter(
        (p: PMCPoint) => p.date <= today
      );
      while (
        points.length > 0 &&
        points[points.length - 1].ctl === 0 &&
        points[points.length - 1].atl === 0 &&
        points[points.length - 1].tsb === 0
      ) {
        points.pop();
      }
      setData(points);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  const handleRangeChange = (r: PMCRange) => {
    setRange(r);
  };

  return (
    <div className="analytics-card" style={{ marginTop: 16 }}>
      <h3>Performance Management Chart</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => handleRangeChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : data.length === 0 ? (
        <div className="analytics-chart-empty">No PMC data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data}>
            <defs>
              <linearGradient id="ctlGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#999' }}
              tickFormatter={(val) => formatDateTick(val)}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 12, fill: '#999' }} />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="line"
              formatter={legendTooltipFormatter(pmcLegendDefs)}
            />
            <Area
              type="monotone"
              dataKey="ctl"
              stroke="none"
              fill="url(#ctlGradient)"
              dot={false}
              legendType="none"
            />
            <Line
              type="monotone"
              dataKey="ctl"
              stroke="#2563eb"
              strokeWidth={2.5}
              dot={false}
              name="CTL (Fitness)"
            />
            <Line
              type="monotone"
              dataKey="atl"
              stroke="#dc2626"
              strokeWidth={1.5}
              dot={false}
              name="ATL (Fatigue)"
            />
            <Line
              type="monotone"
              dataKey="tsb"
              stroke="#16a34a"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              name="TSB (Form)"
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PMCChart;
